import {
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@material-ui/core";
import FieldMeta from "./FieldMeta";
import Card from "../cards/Card";
import ChartGrowerMoisture from "../charts/ChartGrowerMoisture.js";
import Statistic from "../elements/Statistic";
import Image from "../elements/Image";
import { NoFieldsWarning } from "./NoFieldsWarning";
import { useLocalStorage } from "../../helpers/useLocalStorage";
import { capitalizeFirstLetter as capitalise } from "../../helpers";
import { useColumnVisibility } from "../../helpers/useColumnVisibility";
import { useAuthState } from "../../context/auth";

const FieldsCards = ({
  title,
  fields,
  onClick,
  fieldType,
  clientName,
}: {
  title: string;
  fields: any[];
  onClick: (id: string) => void;
  fieldType: string;
  clientName: string;
}) => {
  const user = useAuthState();
  const { isVisible } = useColumnVisibility(user);
  const columnStorageKey = `${clientName}${capitalise(
    fieldType
  )}FieldTableColumns`;
  const [columnOrder] = useLocalStorage<string[]>(columnStorageKey, [], true);

  const sortDataKeys = (data: Record<string, any>) => {
    const keys = Object.keys(data);
    if (!columnOrder.length) return keys;
    return [...keys].sort((a, b) => {
      const aRef = data[a]?.ref;
      const bRef = data[b]?.ref;
      const aIndex = columnOrder.indexOf(aRef);
      const bIndex = columnOrder.indexOf(bRef);
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  };

  return fields ? (
    <>
      <Grid item xs={12}>
        <Typography variant="h1">{title}</Typography>
      </Grid>
      {fields.length ? (
        fields.map((field) => (
          <Grid
            key={`${field.id}-card`}
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            xl={2}
          >
            <Card
              headline={field.name}
              headlineVariant="h2"
              subheadline={<FieldMeta field={field} />}
              subHeadlineVariant="subtitle1"
              divider={true}
              link={() => onClick(field.id)}
            >
              {field.data && (
                <List dense={true}>
                  {field.irrimax && field.irrimax.length && (
                    <Image
                      src={field.irrimax[0].gauge}
                      alt={field.irrimax[0].name}
                    />
                  )}
                  {!field.irrimax && field.data[0]?.value && (
                    <ChartGrowerMoisture
                      id={field.name}
                      level={field.data[0].value}
                      isSoil={fieldType === "soil"}
                      paramData={field.parameters}
                    />
                  )}
                  {sortDataKeys(field.data).map((item) => {
                    const {
                      short_name,
                      ref,
                      value,
                      suffix,
                      thresholds,
                      parameters,
                    } = field.data[item];
                    if (!isVisible(ref)) return null;
                    return (
                      <ListItem
                        key={`${field.id}-stat-${ref}`}
                        disableGutters={true}
                      >
                        <ListItemText primary={`${short_name}: `} />
                        <Statistic
                          {...{
                            type: ref,
                            value,
                            suffix,
                            thresholds,
                            parameters,
                          }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Card>
          </Grid>
        ))
      ) : (
        <Grid item xs={12}>
          <Typography>No fields found.</Typography>
        </Grid>
      )}
    </>
  ) : (
    <NoFieldsWarning />
  );
};

export default FieldsCards;
